<template>
  <div class="product-dialog-content full-height">
    <v-card
      class="d-flex flex-column full-height"
      tile
      :loading="isLoading ? 'secondary' : false"
      :max-height="!isMobile ? '738px' : '100vh'"
      :min-height="!isMobile ? '738px' : ''"
    >
      <v-toolbar
        style="height: auto;"
        elevation="0"
        class="py-3 pr-4 toolbar-with-subheader flex-grow-0 flex-shrink-0"
        color="white"
      >
        <div class="toolbar-titles-container overflow-hidden">
          <template v-if="isLoading">
            <v-toolbar-title class="pl-5 mt-1">
              <v-skeleton-loader
                type="heading"
                style="width: 200px; max-width: 200px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
            <v-toolbar-title class="toolbar-subheader pl-5 mt-2">
              <v-skeleton-loader
                type="text"
                style="width: 120px; max-width: 120px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
          </template>
          <template v-else>
            <v-toolbar-title class="pl-5">{{ product?.name }}</v-toolbar-title>
            <v-toolbar-title class="pl-5 toolbar-subheader">{{ subtitle }}</v-toolbar-title>
          </template>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon @click="startEdit" :disabled="isLoading || !product">
          <v-icon>mdi-pen</v-icon>
        </v-btn>
      </v-toolbar>
      <v-tabs
        v-model="tabIndex"
        color="secondary"
        class="flex-grow-0 flex-shrink-0"
        grow
      >
        <v-tab href="#product-data">
          Information
        </v-tab>
        <v-tab href="#product-history">
          Historik
        </v-tab>
        <v-tab href="#product-sales-stats">
          Statistik
        </v-tab>
        <v-tab href="#product-recent-sales">
          Försäljningar
        </v-tab>
        <v-tab href="#product-cart">
          <v-badge
            :value="cartChargesCount > 0"
            color="secondary"
            :content="cartChargesCount"
            offset-x="-2"
            offset-y="8"
          >
            <span>Varukorgar</span>
          </v-badge>
        </v-tab>
      </v-tabs>
      <v-divider></v-divider>
      <v-tabs-items v-model="tabIndex" class="flex-grow-1 overflow-y-auto" style="min-height: 0;">
        <!-- Tab 1: Produktinformation -->
        <v-tab-item value="product-data">
          <v-sheet class="flex-grow-1 pa-4">
            <template v-if="isLoading">
              <v-card-text class="pa-6">
                <v-skeleton-loader type="list-item-three-line, list-item-three-line"></v-skeleton-loader>
              </v-card-text>
            </template>
            <template v-else-if="product">
              <v-list subheader>
                <v-list-item class="cursor-pointer" @click="openProductGroup">
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-shape</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ productGroupName }}</v-list-item-title>
                    <v-list-item-subtitle>Produktgrupp</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-percent</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ vatRateFormatted }}</v-list-item-title>
                    <v-list-item-subtitle>Momssats</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>coi-currency-sek</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ priceFormatted }}</v-list-item-title>
                    <v-list-item-subtitle>Pris inkl. moms</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-barcode</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ barcodeText }}</v-list-item-title>
                    <v-list-item-subtitle>Streckkod</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-palette</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-chip
                        :color="product.gui_color"
                        dark
                        x-small
                      >
                        {{ productColorName }}
                      </v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle>Produktfärg</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-calendar</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ createdAtFormatted }}</v-list-item-title>
                    <v-list-item-subtitle>Skapad</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-information-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      <template v-if="product.status === 'deleted'">
                        Borttagen {{ deletedAtFormatted }}
                      </template>
                      <template v-else>
                        {{ product.status_text }}
                      </template>
                    </v-list-item-title>
                    <v-list-item-subtitle>Status</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="product.used_on_settings && product.used_on_settings.length > 0">
                  <v-list-item-icon class="my-auto">
                    <v-icon>mdi-cogs</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-chip
                        v-for="(setting, i) in product.used_on_settings"
                        :key="i"
                        class="mr-3"
                        x-small
                        color="primary"
                      >
                        {{ setting.text }}
                      </v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle>Inställningar där produkten används</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
          </v-sheet>
        </v-tab-item>

        <!-- Tab 2: Historik -->
        <v-tab-item value="product-history">
          <v-sheet class="flex-grow-1 pa-4">
            <editing-record-timeline
              :editing-records="editingRecords"
              :loading="isLoading"
            ></editing-record-timeline>
          </v-sheet>
        </v-tab-item>

        <!-- Tab 3: Försäljningsstatistik -->
        <v-tab-item value="product-sales-stats">
          <v-sheet class="flex-grow-1 pa-4">
            <template v-if="isLoading">
              <v-skeleton-loader type="image"></v-skeleton-loader>
            </template>
            <template v-else-if="product">
              <sales-history-chart
                :product-id="product.id"
                :prefetched-data="salesHistory"
              ></sales-history-chart>
              <v-simple-table class="mt-6" v-if="salesHistory">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Månad</th>
                      <th class="text-right">Omsättning</th>
                      <th class="text-right">Sålda enheter</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, i) in salesHistoryReversed" :key="i">
                      <td>{{ row.month.format('MMMM YYYY') }}</td>
                      <td class="text-right">{{ toHumanReadableAmount(row.total_sales / 100) }}</td>
                      <td class="text-right">{{ Math.round(row.total_quantity / 1000) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>
          </v-sheet>
        </v-tab-item>

        <!-- Tab 4: Senaste försäljningar -->
        <v-tab-item value="product-recent-sales">
          <v-sheet class="flex-grow-1">
            <template v-if="isLoadingRecentSales && recentSalesItems.length === 0">
              <v-card-text>
                <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
              </v-card-text>
            </template>
            <template v-else-if="recentSalesItems.length > 0">
              <v-simple-table fixed-header>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Typ</th>
                      <th>Referens</th>
                      <th class="text-right">Antal</th>
                      <th class="text-right">Pris</th>
                      <th class="text-right">Total</th>
                      <th>&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="salesItem in recentSalesItems"
                      :key="salesItem.id"
                      class="cursor-pointer"
                      @click="openSalesItemOwner(salesItem)"
                    >
                      <td>{{ formatDateTime(salesItem.created_at) }}</td>
                      <td>{{ salesItemType(salesItem) }}</td>
                      <td>{{ salesItemReference(salesItem) }}</td>
                      <td class="text-right">{{ salesItem.quantity / 1000 }}</td>
                      <td class="text-right">{{ toHumanReadableAmount(salesItem.price / 100) }}</td>
                      <td class="text-right">{{ toHumanReadableAmount(salesItem.sale / 100) }}</td>
                      <td style="width: 0;">
                        <template v-if="product?.should_have_delivery_date && !salesItem.delivery_date">
                          <simple-tooltip tooltip="Saknar leveransdatum">
                            <v-icon
                              color="warning"
                            >
                              mdi-alert-outline
                            </v-icon>
                          </simple-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <div class="d-flex justify-center pa-4" v-if="recentSalesItems.length < recentSalesTotalCount">
                <v-btn
                  text
                  color="secondary"
                  :loading="isLoadingRecentSales"
                  @click="loadMoreRecentSales"
                >
                  Ladda fler ({{ recentSalesItems.length }} av {{ recentSalesTotalCount }})
                </v-btn>
              </div>
            </template>
            <template v-else>
              <v-alert
                dense
                text
                color="info"
                class="ma-4 text-center"
              >
                Inga försäljningar har registrerats för denna produkt
              </v-alert>
            </template>
          </v-sheet>
        </v-tab-item>

        <!-- Tab 5: I varukorgar -->
        <v-tab-item value="product-cart">
          <v-sheet class="flex-grow-1">
            <template v-if="isLoadingCartCharges">
              <v-card-text>
                <v-skeleton-loader type="table-row@3"></v-skeleton-loader>
              </v-card-text>
            </template>
            <template v-else-if="cartCharges.length > 0">
              <v-simple-table fixed-header>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Tillhör</th>
                      <th>Referens</th>
                      <th class="text-right">Antal</th>
                      <th class="text-right">Pris</th>
                      <th class="text-right">Total</th>
                      <th>Skapad</th>
                      <th>&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="charge in cartCharges"
                      :key="charge.id"
                      :class="{ 'cursor-pointer': isChargeClickable(charge) }"
                      @click="openChargeOwner(charge)"
                    >
                      <td>{{ chargeOwnerType(charge) }}</td>
                      <td>{{ chargeOwnerReference(charge) }}</td>
                      <td class="text-right">{{ charge.quantity / 1000 }}</td>
                      <td class="text-right">{{ toHumanReadableAmount(charge.price / 100) }}</td>
                      <td class="text-right">{{ toHumanReadableAmount(charge.total / 100) }}</td>
                      <td>{{ formatDateTime(charge.created_at) }}</td>
                      <td style="width: 0;">
                        <template v-if="product?.should_have_delivery_date && !charge.delivery_date">
                          <simple-tooltip tooltip="Saknar leveransdatum">
                            <v-icon
                              color="warning"
                            >
                              mdi-alert-outline
                            </v-icon>
                          </simple-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>
            <template v-else>
              <v-alert
                dense
                text
                color="info"
                class="ma-4 text-center"
              >
                Produkten finns inte i någon varukorg
              </v-alert>
            </template>
          </v-sheet>
        </v-tab-item>
      </v-tabs-items>
      <v-card-actions
        class="pa-4"
      >
        <v-btn
          @click="deletedProduct"
          :loading="isDeleting"
          text
          color="error"
        >
          Ta bort produkt
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import SalesHistoryChart from '@/components/product/SalesHistoryChart.vue'
import EditingRecordTimeline from '@/components/common/EditingRecordTimeline.vue'
import ProductService from '@/services/ProductService.js'
import SimpleTooltip from '@/components/common/SimpleTooltip.vue'

export default {
  name: 'ProductDialogContent',
  components: {
    SalesHistoryChart,
    EditingRecordTimeline,
    SimpleTooltip
  },
  props: {
    product: Object,
    salesHistory: Array,
    editingRecords: {
      type: Array,
      default: () => []
    },
    isLoading: Boolean,
    isMobile: Boolean,
    cartCharges: {
      type: Array,
      default: () => []
    },
    isLoadingCartCharges: Boolean,
    show: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    tabIndex: 'product-data',
    recentSalesItems: [],
    recentSalesTotalCount: 0,
    isLoadingRecentSales: false,
    isDeleting: false
  }),
  computed: {
    ...mapState(
      {
        productGroups: state => state.product.productGroups,
        vuetifyColors: state => state.vuetifyColors
      }
    ),
    subtitle: function () {
      if (!this.product) {
        return ''
      }
      const formattedDateTime = this.product.created_at.toHumanReadableDate({ capitalized: false })
      return `Produkt skapad ${formattedDateTime}`
    },
    productColorName: function () {
      if (!this.product || !this.product.gui_color) {
        return 'Ingen färg vald'
      }
      const vuetifyColor = this.vuetifyColors.find(c => c.hex === this.product.gui_color || c.name === this.product.gui_color)
      return vuetifyColor ? vuetifyColor.name_sv : this.product.gui_color
    },
    barcodeText: function () {
      if (this.product && this.product.barcode) {
        return this.product.barcode
      }
      return 'Saknar streckkod'
    },
    priceFormatted: function () {
      if (!this.product || this.product.price === null) {
        return 'Saknar bestämt pris'
      }
      return this.toHumanReadableAmount(this.product.price / 100)
    },
    productGroupName: function () {
      if (!this.product) {
        return ''
      }
      const productGroup = this.productGroups.find(pg => pg.id === this.product.product_group_id)
      return productGroup ? productGroup.name : ''
    },
    vatRateFormatted: function () {
      if (!this.product) {
        return ''
      }
      const productGroup = this.productGroups.find(pg => pg.id === this.product.product_group_id)
      if (productGroup && productGroup.tax) {
        return productGroup.tax.name
      }
      return ''
    },
    createdAtFormatted: function () {
      if (!this.product) {
        return ''
      }
      return this.product.created_at.toHumanReadableDate()
    },
    deletedAtFormatted: function () {
      if (!this.product || !this.product.deleted_at) {
        return ''
      }
      return this.product.deleted_at.toHumanReadableDate({ capitalized: false })
    },
    salesHistoryReversed: function () {
      if (!this.salesHistory) {
        return []
      }
      return [...this.salesHistory].reverse()
    },
    cartChargesCount: function () {
      return this.cartCharges.length
    }
  },
  methods: {
    deletedProduct: function () {
      if (!this.product) {
        return
      }
      this.isDeleting = true
      this.$store.dispatch('product/deleteProduct', { product: this.product })
        .then((data) => {
          if (data.status === 'success') {
            this.$emit('close') // Stänger bara dialogen om det gick att ta bort produkten
          }
        })
        .finally(() => {
          this.isDeleting = false
        })
    },
    toHumanReadableAmount: function (amount) {
      return window.conversion.toHumanReadableAmount(amount)
    },
    formatDateTime: function (dt) {
      return window.dayjs.parseServerDateTime(dt).format('YYYY-MM-DD HH:mm')
    },
    formatPrice: function (priceInOre) {
      if (priceInOre === null) {
        return 'Saknar bestämt pris'
      }
      return this.toHumanReadableAmount(priceInOre / 100)
    },
    salesItemType: function (salesItem) {
      if (salesItem.receipt_id) {
        return 'Kvitto'
      }
      if (salesItem.invoice_id) {
        return 'Faktura'
      }
      if (salesItem.credit_invoice_id) {
        return 'Kreditfaktura'
      }
      return ''
    },
    salesItemReference: function (salesItem) {
      if (salesItem.receipt_id && salesItem.receipt) {
        return `#${salesItem.receipt_id}`
      }
      if (salesItem.invoice_id && salesItem.invoice) {
        return salesItem.invoice.invoice_number
      }
      if (salesItem.credit_invoice_id && salesItem.credit_invoice) {
        return salesItem.credit_invoice.invoice_number
      }
      return ''
    },
    openSalesItemOwner: function (salesItem) {
      if (salesItem.receipt_id) {
        this.$store.dispatch('posReceipt/openDialogById', { receiptId: salesItem.receipt_id, startTab: 'sales-items' })
      } else if (salesItem.invoice_id) {
        this.$store.dispatch('invoice/openDialogById', { invoiceId: salesItem.invoice_id, startTab: 'sales-items' })
      } else if (salesItem.credit_invoice_id) {
        this.$store.dispatch('invoice/openDialogById', { invoiceId: salesItem.credit_invoice_id, startTab: 'sales-items' })
      }
    },
    chargeOwnerType: function (charge) {
      if (charge.booking_id && charge.booking) {
        return 'Bokning'
      }
      if (charge.booking_group_id && charge.booking_group) {
        return 'Gruppbokning'
      }
      if (charge.parked_order_id && charge.parked_order) {
        return 'Parkerad nota'
      }
      return 'Okänd'
    },
    chargeOwnerReference: function (charge) {
      if (charge.booking_id && charge.booking) {
        return charge.booking.booking_number
      }
      if (charge.booking_group_id && charge.booking_group) {
        return charge.booking_group.booking_number
      }
      if (charge.parked_order_id && charge.parked_order) {
        return charge.parked_order.name
      }
      return ''
    },
    openChargeOwner: function (charge) {
      if (charge.booking_id) {
        this.$store.dispatch('booking/openDialogById', { bookingId: charge.booking_id, startTab: 'other-charges' })
      } else if (charge.booking_group_id) {
        this.$store.dispatch('bookingGroup/openDialogById', { bookingGroupId: charge.booking_group_id, startTab: 'other-charges' })
      }
    },
    isChargeClickable: function (charge) {
      return !!(charge.booking_id || charge.booking_group_id)
    },
    openProductGroup: function () {
      if (!this.product) {
        return
      }
      this.$store.dispatch('product/openProductGroupDialogById', { productGroupId: this.product.product_group_id, smaller: true })
    },
    startEdit: function () {
      if (!this.product) {
        return
      }
      this.$store.dispatch('product/startEditProduct', { product: this.product })
    },
    fetchRecentSalesItems: function () {
      if (!this.product) {
        return
      }
      this.isLoadingRecentSales = true
      ProductService.getRecentSalesItems({
        productId: this.product.id,
        offset: this.recentSalesItems.length,
        limit: 20
      })
        .then(({ data }) => {
          if (data.status === 'success') {
            this.recentSalesItems = this.recentSalesItems.concat(data.data.sales_items)
            this.recentSalesTotalCount = data.data.total_count
          }
        })
        .finally(() => {
          this.isLoadingRecentSales = false
        })
    },
    loadMoreRecentSales: function () {
      this.fetchRecentSalesItems()
    }
  },
  watch: {
    product: function () {
      this.recentSalesItems = []
      this.recentSalesTotalCount = 0
    },
    tabIndex: function (newTab) {
      if (newTab === 'product-recent-sales' && this.recentSalesItems.length === 0 && this.product) {
        this.fetchRecentSalesItems()
      }
    },
    show: function () {
      if (!this.show) {
        this.tabIndex = 'product-data'
      }
    }
  }
}
</script>
